import React, { useEffect, useRef } from 'react';
import { SlotCard } from '../types/types';
import { CARD_ICONS } from '../utils/cardConstants';
import '../styles/Slot.css';

// Import all HP icons
import glassesIcon from '../assets/hp_icons/glasses.png';
import hatIcon from '../assets/hp_icons/hat.png';
import scrollIcon from '../assets/hp_icons/scroll.png';
import owlIcon from '../assets/hp_icons/owl.png';
import letterIcon from '../assets/hp_icons/letter.png';
import timeturnerIcon from '../assets/hp_icons/timeturner.png';
import wandIcon from '../assets/hp_icons/wand.png';
import beerIcon from '../assets/hp_icons/beer.png';
import bookIcon from '../assets/hp_icons/book.png';
import goldenSnitchIcon from '../assets/hp_icons/golden-snitch.png';
import cauldronIcon from '../assets/hp_icons/cauldron.png';
import scarfIcon from '../assets/hp_icons/scarf.png';
import hogwartsCrestIcon from '../assets/hp_icons/hogwarts-crest.png';
import potionIcon from '../assets/hp_icons/potion.png';

// Map icon types to their image files
const iconImages: Record<string, string> = {
  'glasses': glassesIcon,
  'hat': hatIcon,
  'scroll': scrollIcon,
  'owl': owlIcon,
  'letter': letterIcon,
  'timeturner': timeturnerIcon,
  'wand': wandIcon,
  'beer': beerIcon,
  'book': bookIcon,
  'golden-snitch': goldenSnitchIcon,
  'cauldron': cauldronIcon,
  'scarf': scarfIcon,
  'hogwarts-crest': hogwartsCrestIcon,
  'potion': potionIcon
};

interface SlotProps {
  cards: SlotCard[];
}

const Slot: React.FC<SlotProps> = ({ cards }) => {
  // Keep track of last card count to debug re-renders
  const cardCountRef = useRef<number>(0);
  
  useEffect(() => {
    // Log when the number of cards changes for debugging
    if (cardCountRef.current !== cards.length) {
      console.log(`Slot cards changed from ${cardCountRef.current} to ${cards.length}`, 
                 cards.map(c => `${c.type}(${c.id})`).join(', '));
      cardCountRef.current = cards.length;
    }
    
    // Clean up any lingering animation classes that might affect rendering
    const cleanup = () => {
      const animatedElements = document.querySelectorAll('.match-animation, .revert-animation');
      animatedElements.forEach(element => {
        element.classList.remove('match-animation', 'revert-animation');
      });
    };
    
    // Run cleanup on cards change
    cleanup();
    
    // Also run cleanup when component unmounts
    return cleanup;
  }, [cards]);

  // Ensure we render proper cards after each update
  useEffect(() => {
    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(() => {
      // Double-check animation cleanup and card rendering
      const slotPositions = document.querySelectorAll('.slot-position');
      
      // Verify each slot position has the right content
      slotPositions.forEach((position, index) => {
        const card = cards[index];
        const slotCard = position.querySelector('.slot-card');
        const emptySlot = position.querySelector('.empty-slot');
        
        // If there should be a card but shows empty or vice versa
        if ((card && !slotCard) || (!card && !emptySlot)) {
          console.log(`Slot rendering mismatch at index ${index}, forcing refresh`);
          // Force a refresh if needed
        }
        
        // Make sure no cards have lingering animations
        if (slotCard) {
          slotCard.classList.remove('match-animation', 'revert-animation');
        }
      });
    }, 50);
    
    return () => clearTimeout(timeoutId);
  }, [cards]);

  // Create an array of cards or empty slots to be rendered
  const slotContents = Array(7).fill(null).map((_, index) => {
    return cards[index] || null;
  });

  // Render 7 slot positions, filled or empty
  return (
    <div className="slot-container">
      {slotContents.map((card, index) => {
        // Generate unique keys that change when cards change
        const cardKey = card ? `card-${card.id}-${index}` : `empty-${index}`;
        
        return (
          <div key={`slot-${index}-${card ? card.id : 'empty'}`} className="slot-position">
            {card ? (
              <div 
                className="slot-card" 
                data-index={index} 
                data-card-id={card.id}
                data-card-type={card.type}
                key={cardKey}
              >
                <div className={`slot-card-icon ${CARD_ICONS[card.type % CARD_ICONS.length]}`}>
                  <img 
                    src={iconImages[CARD_ICONS[card.type % CARD_ICONS.length]]} 
                    alt={CARD_ICONS[card.type % CARD_ICONS.length]} 
                    className="slot-card-icon-image" 
                  />
                </div>
              </div>
            ) : (
              <div className="empty-slot" key={cardKey}></div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Slot; 